import { Table, Popup, Icon } from 'semantic-ui-react';
import { PricingType, DealStates, UnitDecimals, ContractType } from '~/constants';
import { useUserContext } from '~/hooks';
import { calculateProjectCosts } from '~/utils/calculators';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { sizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { formatDollars } from '~/utils/formatNumber';
import { offtakeBidDecimals } from '~/utils';

import '../../styles.scss';

// Shown when a project has no PV wattage or storage to base a fee on.
export const NO_FEE_BASIS_MESSAGE = 'There is no wattage/system to base the fee on.';

interface BudgetTableProps {
  project: any;
  formState?: any;
  viewMode?: 'table' | 'card' | 'summary';
  budgetDisplayMode?: 'unit' | 'total';
  uiContext?: string;
}

export default function BudgetTable({
  project,
  formState,
  viewMode = 'table',
  budgetDisplayMode = 'unit',
  uiContext = 'project'
}: BudgetTableProps) {

  const { loggedInUser } = useUserContext();
  const { permissions } = usePermissions();
  const isStorageOnly = getProjectStorageSizeKwh(project) > 0 && sizeKwdc(project) === 0;
  // No PV wattage and no storage discharge means there is nothing to base a fee on.
  const hasNoFeeBasis = sizeKwdc(project) === 0 && getProjectStorageSizeKwh(project) === 0;
  const isEpcOperating = project?.pricingType === PricingType.ACQUISITION && project?.developmentStage === 'OPERATING';
  const isDeclined = project?.status === 'BID_REJECTED' || project?.status === 'DECLINED_TO_BID';
  const placeholder = isDeclined ? 'Declined' : 'TBD';
  const showOnlyFees = project?.pricingType === PricingType.OFFTAKE && viewMode !== 'summary';
  let projectCosts: any;
  if (project && project.acceptedBid) {
    projectCosts = calculateProjectCosts(
      project,
      permissions,
      { bid: project.acceptedBid }
    );
  } else {
    projectCosts = calculateProjectCosts(
      project,
      permissions,
      { bid: formState }
    );
  }


  const epcLabel = (() => {
    let label = 'EPC cost + dev fee';
    if (project.pricingType === PricingType.DEV_FEE) {
      label = 'Development fee';
    }
    if (project.developmentStage === 'OPERATING') {
      label = 'Project acquisition';
    }

    if (loggedInUser?.isInvestor && (project.pricingType === PricingType.ACQUISITION || project.pricingType === PricingType.DEV_FEE)) {
      label += ' (Your bid)';
    } else if (!loggedInUser?.isInvestor && (project.pricingType === PricingType.ACQUISITION || project.pricingType === PricingType.DEV_FEE)) {
      label += ' (Investor bid)';
    }

    if ((project.pricingType === PricingType.DEV_FEE || project.pricingType === PricingType.ACQUISITION) && viewMode === 'card' && uiContext === 'portfolio') {
      label = project.name;
    }

    return label;
  })();

  // Check if project is in diligence or later (seller fees are visible)
  const isInDiligenceOrLater = () => {
    return project?.dealState >= DealStates.IN_DILIGENCE;
  };

  const totalsLabel = loggedInUser.isInvestor ?
    (project?.pricingType === 'OFFTAKE' ? 'Project totals' : 'Total cost to you') :
    (project?.pricingType === 'OFFTAKE' ? 'Project totals' : 'Net proceeds');

  const getPopupContent = (feeType: 'buyer' | 'seller') => {
    return (
      <div>
        {feeType === 'buyer' ? (
          <div>
            {(projectCosts.total.nrsFee === 0) && (projectCosts.total.buyerStorageFee === 0) ? (
              <div>{hasNoFeeBasis ? NO_FEE_BASIS_MESSAGE : 'Seller pays fees on this deal.'}</div>
            ) : (
              <div>
                {projectCosts.data.buyerFees && projectCosts.data.buyerFees.nrsFeeUnit === 'PERCENT_OF_BUDGET' && !isStorageOnly ? (
                  <>
                    <strong>Fee:</strong> {projectCosts.data.buyerFees.nrsFee || 0}% of project budget
                  </>
                ) : (
                  <>
                    {!isStorageOnly ? (
                      <div>
                        <strong style={{ marginRight: '4px' }}>PV Fee:</strong>
                        {`${formatDollars(projectCosts.perWdc.nrsFee || 0, { dec: UnitDecimals.WDC })}/Wdc or ${formatDollars(projectCosts.total.nrsFee || 0, { dec: UnitDecimals.TOTAL })}`}
                      </div>
                    ) : null}
                    {projectCosts.total.buyerStorageFee && projectCosts.total.buyerStorageFee > 0 ? (
                      <div>
                        <strong style={{ marginRight: '4px' }}>BESS Fee:</strong>
                        {
                          `${formatDollars(projectCosts.perKwh.buyerStorageFee || 0, { dec: UnitDecimals.KWH_BESS })}/kWh 
                      ${isStorageOnly ? '' : `(${formatDollars(projectCosts.perWdc.buyerStorageFee || 0, { dec: UnitDecimals.WDC })}/Wdc)`} 
                      or ${formatDollars(projectCosts.total.buyerStorageFee || 0, { dec: UnitDecimals.TOTAL })}`
                        }
                      </div>
                    ) : null}
                  </>
                )}

              </div>
            )}
          </div>
        ) : (
          <div>
            {(!projectCosts.total.sellerFee || projectCosts.total.sellerFee === 0) && (!projectCosts.total.sellerStorageFee || projectCosts.total.sellerStorageFee === 0) ? (
              <div>{hasNoFeeBasis ? NO_FEE_BASIS_MESSAGE : 'Buyer pays fees on this deal.'}</div>
            ) : (
              <div>
                {!isStorageOnly ? (
                  <div>
                    <strong style={{ marginRight: '4px' }}>PV Fee:</strong>
                    {`${formatDollars(projectCosts.perWdc.sellerFee || 0, { dec: UnitDecimals.WDC })}/Wdc or ${formatDollars(projectCosts.total.sellerFee || 0, { dec: UnitDecimals.TOTAL })}`}
                  </div>
                ) : null}
                {projectCosts.total.sellerStorageFee && projectCosts.total.sellerStorageFee > 0 ? (
                  <div>
                    <strong style={{ marginRight: '4px' }}>BESS Fee:</strong>
                    {
                      `${formatDollars(projectCosts.perKwh.sellerStorageFee || 0, { dec: UnitDecimals.KWH_BESS })}/kWh 
                      ${isStorageOnly ? '' : `(${formatDollars(projectCosts.perWdc.sellerStorageFee || 0, { dec: UnitDecimals.WDC })}/Wdc)`} 
                      or ${formatDollars(projectCosts.total.sellerStorageFee || 0, { dec: UnitDecimals.TOTAL })}`
                    }
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const showEpcCosts = (viewMode !== 'summary' && projectCosts.bid && projectCosts.bid.amount > 0) || (isInDiligenceOrLater() && (project?.pricingType === PricingType.ACQUISITION || project?.pricingType === PricingType.DEV_FEE)) || project?.pricingType === PricingType.OFFTAKE;
  const showOfftakeTotals = (viewMode !== 'summary' && projectCosts.bid && projectCosts.bid.amount > 0) || (isInDiligenceOrLater() && project?.pricingType === PricingType.OFFTAKE);

  const showOfftakeSection = project.pricingType === PricingType.OFFTAKE && (
    viewMode === 'summary' ||
    viewMode === 'table' ||
    (viewMode === 'card' && uiContext === 'portfolio')
  );

  const showBuyerFeeValues = () => {
    let showBuyerFeeValues = true;
    if (isStorageOnly) {
      showBuyerFeeValues = true;
    } else {
      if (projectCosts.bid && projectCosts.bid.amount > 0 && projectCosts.data.buyerFees && projectCosts.data.buyerFees.nrsFeeUnit === 'PERCENT_OF_BUDGET') {
        showBuyerFeeValues = true;
      }
      if (
        projectCosts.total.nrsFee &&
        projectCosts.total.nrsFee > 0 &&
        projectCosts.data.buyerFees.nrsFeeUnit === 'PERCENT_OF_BUDGET' &&
        !projectCosts.bid
      ) {
        showBuyerFeeValues = false;
      }
      if (projectCosts.data.sellerFees) {
        showBuyerFeeValues = true;
      }
    }
    return showBuyerFeeValues;
  };

  const getEpcCostDisplayValues = () => {
    if (project.pricingType === PricingType.ACQUISITION || project.pricingType === PricingType.DEV_FEE) {
      return {
        total: projectCosts.total.bid || 'TBD',
        perWdc: projectCosts.perWdc.bid || 'TBD',
        perKwh: projectCosts.perKwh.bid || 'TBD'
      };
    } else {
      return {
        total: projectCosts.total.epcCost,
        perWdc: projectCosts.perWdc.epcCost,
        perKwh: projectCosts.perKwh.epcCost
      };
    }
  };

  const epcCostDisplayValues = getEpcCostDisplayValues();


  const getFeeTotals = () => {

    const { perKwh, perWdc, total } = projectCosts;
    const results = {
      buyer: {
        total: (total.nrsFee || 0) + (total.buyerStorageFee || 0),
        perWdc: (perWdc.nrsFee || 0) + (perWdc.buyerStorageFee || 0),
        perKwh: (perKwh.buyerStorageFee || 0)
      },
      seller: {
        total: (total.sellerFee || 0) + (total.sellerStorageFee || 0),
        perWdc: (perWdc.sellerFee || 0) + (perWdc.sellerStorageFee || 0),
        perKwh: (perKwh.sellerStorageFee || 0)
      }
    };

    return results;
  };

  const feeTotals = getFeeTotals();
  const showEpcCostSection = viewMode === 'summary' ||
    viewMode === 'table' ||
    (viewMode === 'card' && project.pricingType !== 'OFFTAKE');

  return (
    <div className={`budget-table-container ${viewMode === 'card' ? 'card-view' : ''}`}>
      {showOfftakeSection ? (
        <Table compact className="budget-table" style={viewMode === 'card' ? { margin: '0' } : {}}>
          {viewMode !== 'card' ? (
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  Item
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                  Total
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
          ) : null}
          <Table.Body>
            <Table.Row>
              <Table.Cell style={viewMode === 'card' ? { background: '#fff' } : {}}>
                {viewMode === 'card' ? (
                  <>
                    {project.name}
                  </>
                ) : (
                  <>
                    Offtake rate ({loggedInUser?.isInvestor ? 'Your bid' : 'Investor bid'})
                  </>
                )}

              </Table.Cell>
              <Table.Cell textAlign="right" style={viewMode === 'card' ? { background: '#fff' } : {}}>
                {
                  showOfftakeTotals ?
                    `${formatDollars(projectCosts.bid.amount, { dec: offtakeBidDecimals(project?.contractType) })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? (project?.contractType === ContractType.PPA ? '/kWh' : '/year') : ''}`
                    : placeholder
                }
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      ) : null
      }

      <Table className="budget-table" compact style={viewMode === 'card' ? { margin: '0' } : {}}>

        {viewMode !== 'card' ? (
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                Item
              </Table.HeaderCell>
              {isStorageOnly ? (
                <Table.HeaderCell textAlign="right">
                  $/kWh
                </Table.HeaderCell>
              ) : (
                <Table.HeaderCell textAlign="right">
                  $/Wdc
                </Table.HeaderCell>
              )}
              <Table.HeaderCell textAlign="right">
                Total
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
        ) : null}

        <Table.Body>
          {showEpcCostSection ? (
            <Table.Row className={(project?.pricingType === 'ACQUISITION' || project?.pricingType === 'DEV_FEE') && uiContext !== 'portfolio' ? 'highlight' : ''}>
              <Table.Cell>
                {epcLabel}
              </Table.Cell>
              {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                <Table.Cell textAlign="right">{showEpcCosts ? formatDollars(epcCostDisplayValues.total, { dec: UnitDecimals.TOTAL }) : placeholder}</Table.Cell>
              ) : (
                <>
                  {isStorageOnly ? (
                    <Table.Cell textAlign="right">{showEpcCosts ? `${formatDollars(epcCostDisplayValues.perKwh, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}` : placeholder}</Table.Cell>
                  ) : (
                    <Table.Cell textAlign="right">{showEpcCosts ? `${formatDollars(epcCostDisplayValues.perWdc, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}` : placeholder}</Table.Cell>
                  )}
                  {viewMode !== 'card' ? (
                    <Table.Cell textAlign="right">{showEpcCosts ? formatDollars(epcCostDisplayValues.total, { dec: UnitDecimals.TOTAL }) : placeholder}</Table.Cell>
                  ) : null}
                </>
              )}

            </Table.Row>
          ) : null}

          {!isEpcOperating ? (
            <>
              {(viewMode === 'card' && uiContext === 'project' && !showOnlyFees) || viewMode !== 'card' ? (
                <>
                  <Table.Row>
                    <Table.Cell>Interconnection</Table.Cell>
                    {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                      <Table.Cell textAlign="right">{formatDollars(projectCosts.total.interconnection, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                    ) : (
                      <>
                        {isStorageOnly ? (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perKwh.interconnection, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}`}</Table.Cell>
                        ) : (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perWdc.interconnection, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}`}</Table.Cell>
                        )}
                        {viewMode !== 'card' ? (
                          <Table.Cell textAlign="right">{formatDollars(projectCosts.total.interconnection, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                        ) : null}
                      </>
                    )}
                  </Table.Row>

                  <Table.Row>
                    <Table.Cell>{project.pricingType === PricingType.DEV_FEE ? 'Estimated EPC costs' : 'Other ITC-eligible costs'}</Table.Cell>
                    {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                      <Table.Cell textAlign="right">{formatDollars(projectCosts.total.otherItc, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                    ) : (
                      <>
                        {isStorageOnly ? (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perKwh.otherItc, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}`}</Table.Cell>
                        ) : (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perWdc.otherItc, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}`}</Table.Cell>
                        )}
                        {viewMode !== 'card' ? (
                          <Table.Cell textAlign="right">{formatDollars(projectCosts.total.otherItc, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                        ) : null}
                      </>
                    )}

                  </Table.Row>

                  <Table.Row>
                    <Table.Cell>Non-ITC eligible costs</Table.Cell>
                    {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                      <Table.Cell textAlign="right">{formatDollars(projectCosts.total.otherNonItc, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                    ) : (
                      <>
                        {isStorageOnly ? (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perKwh.otherNonItc, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}`}</Table.Cell>
                        ) : (
                          <Table.Cell textAlign="right">{`${formatDollars(projectCosts.perWdc.otherNonItc, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}`}</Table.Cell>
                        )}
                        {viewMode !== 'card' ? (
                          <Table.Cell textAlign="right">{formatDollars(projectCosts.total.otherNonItc, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                        ) : null}
                      </>
                    )}
                  </Table.Row>
                </>
              ) : null}
            </>
          ) : null}

          {(project?.dealState >= DealStates.MATCHING && uiContext !== 'portfolio') ? (
            <>
              {loggedInUser?.isAdmin || loggedInUser?.isInstaller ? (
                <Table.Row>
                  <Table.Cell>
                    Seller fees {viewMode !== 'card' ? 'paid to Conductor' : ''}
                    <Popup
                      trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                      content={getPopupContent('seller')}
                      position="top center"
                      inverted
                      wide="very"
                    />
                  </Table.Cell>
                  {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                    <Table.Cell textAlign="right">-({formatDollars(feeTotals.seller.total, { dec: UnitDecimals.TOTAL })})</Table.Cell>
                  ) : (
                    <>
                      {isStorageOnly ? (
                        <Table.Cell textAlign="right">{`-${formatDollars(feeTotals.seller.perKwh, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}`}</Table.Cell>
                      ) :
                        <Table.Cell textAlign="right">{`-${formatDollars(feeTotals.seller.perWdc, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}`}</Table.Cell>
                      }
                      {viewMode !== 'card' ? (
                        <Table.Cell textAlign="right">-{formatDollars(feeTotals.seller.total, { dec: UnitDecimals.TOTAL })}</Table.Cell>
                      ) : null}
                    </>
                  )}
                </Table.Row>
              ) : null}

              {loggedInUser?.isAdmin || loggedInUser?.isInvestor ? (
                <Table.Row>
                  <Table.Cell>
                    Buyer fees {viewMode !== 'card' ? 'paid to Conductor' : ''}
                    <Popup
                      trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                      content={getPopupContent('buyer')}
                      position="top center"
                      inverted
                      wide="very"
                    />
                  </Table.Cell>
                  {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                    <Table.Cell textAlign="right">{showBuyerFeeValues() ? formatDollars(feeTotals.buyer.total, { dec: UnitDecimals.TOTAL }) : 'TBD'}</Table.Cell>
                  ) : (
                    <>
                      {isStorageOnly ? (
                        <Table.Cell textAlign="right">{showBuyerFeeValues() ? `${formatDollars(feeTotals.buyer.perKwh, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}` : 'TBD'}</Table.Cell>
                      ) :
                        <Table.Cell textAlign="right">{showBuyerFeeValues() ? `${formatDollars(feeTotals.buyer.perWdc, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}` : 'TBD'}</Table.Cell>
                      }
                      {viewMode !== 'card' ? (
                        <Table.Cell textAlign="right">{showBuyerFeeValues() ? formatDollars(feeTotals.buyer.total, { dec: UnitDecimals.TOTAL }) : 'TBD'}</Table.Cell>
                      ) : null}
                    </>
                  )}
                </Table.Row>
              ) : null}
            </>
          ) : null}

          {!isEpcOperating ? (
            <>
              {project?.pricingType !== PricingType.DEV_FEE && ((viewMode === 'card' && uiContext === 'project' && !showOnlyFees) || viewMode !== 'card') ? (
                <Table.Row style={{ fontWeight: 'bold' }}>
                  <Table.Cell>{totalsLabel}</Table.Cell>
                  {viewMode === 'card' && budgetDisplayMode === 'total' ? (
                    <Table.Cell textAlign="right">{showEpcCosts ? formatDollars(projectCosts.total.total, { dec: UnitDecimals.TOTAL }) : 'TBD'}</Table.Cell>
                  ) : (
                    <>
                      {isStorageOnly ? (
                        <Table.Cell textAlign="right">{showEpcCosts ? `${formatDollars(projectCosts.perKwh.total, { dec: UnitDecimals.KWH_BESS })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/kWh' : ''}` : 'TBD'}</Table.Cell>
                      ) : (
                        <Table.Cell textAlign="right">{showEpcCosts ? `${formatDollars(projectCosts.perWdc.total, { dec: UnitDecimals.WDC })}${viewMode === 'card' && budgetDisplayMode === 'unit' ? '/Wdc' : ''}` : 'TBD'}</Table.Cell>
                      )}
                      {viewMode !== 'card' ? (
                        <Table.Cell textAlign="right">{showEpcCosts ? formatDollars(projectCosts.total.total, { dec: UnitDecimals.TOTAL }) : 'TBD'}</Table.Cell>
                      ) : null}
                    </>
                  )}
                </Table.Row>
              ) : null}
            </>
          ) : null}

        </Table.Body>
      </Table>
    </div >
  );
};